import axiosInstance from './axiosConfig';

// ✅ role normalizer (supports SUPERADMIN, superadmin, Admin, etc.)
const normalizeRole = (role) => role?.toLowerCase();

const dashboardService = {
  /**
   * Get all credits data for dashboard calculations
   */
  getCredits: async (limit = 10000, startDate = null, endDate = null) => {
    try {
      const params = { limit };
      // Convert date format from YYYY-MM-DD to YYYY.MM.DD
      if (startDate) params.startDate = startDate.replace(/-/g, '.');
      if (endDate) params.endDate = endDate.replace(/-/g, '.');

      console.log('Fetching credits with params:', params);
      const response = await axiosInstance.get('/credits/', { params });
      const res = response.data;
      
      console.log('Credits API response:', {
        responseType: typeof res,
        isArray: Array.isArray(res),
        hasData: !!res.data,
        sampleItem: Array.isArray(res) ? res[0] : (Array.isArray(res.data) ? res.data[0] : null)
      });

      // ✅ normalize API response
      if (Array.isArray(res)) return res;
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.data?.credits)) return res.data.credits;
      return [];
    } catch (error) {
      console.error('Error fetching credits:', error);
      throw error;
    }
  },

  /**
   * Get all cash sales data for dashboard calculations
   */
  getCashSales: async (limit = 10000, startDate = null, endDate = null) => {
    try {
      const params = { limit };
      // Convert date format from YYYY-MM-DD to YYYY.MM.DD
      if (startDate) params.fromDate = startDate.replace(/-/g, '.');
      if (endDate) params.toDate = endDate.replace(/-/g, '.');

      console.log('Fetching cash sales with params:', params);
      const response = await axiosInstance.get('/sales/cashh', { params });
      const res = response.data;
      
      console.log('Cash sales API response:', {
        responseType: typeof res,
        isArray: Array.isArray(res),
        hasData: !!res.data,
        sampleItem: Array.isArray(res) ? res[0] : (Array.isArray(res.data) ? res.data[0] : null)
      });

      // ✅ normalize API response
      if (Array.isArray(res)) return res;
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.data?.sales)) return res.data.sales;
      return [];
    } catch (error) {
      console.error('Error fetching cash sales:', error);
      throw error;
    }
  },

  /**
   * Get cash management data from cash sales endpoint
   * This provides: totalCash, totalCollection, totalChequeCount, totalChequeAmount
   * Data comes from the "totals" object in /sales/cashh response
   */
  getCashManagement: async (startDate = null, endDate = null) => {
    try {
      const params = { limit: 10000 };
      // Convert date format from YYYY-MM-DD to YYYY.MM.DD
      if (startDate) params.fromDate = startDate.replace(/-/g, '.');
      if (endDate) params.toDate = endDate.replace(/-/g, '.');

      console.log('Fetching cash management with params:', params);
      const response = await axiosInstance.get('/sales/cashh', { params });
      const res = response.data;
      
      console.log('Cash management API response:', res);

      // Extract data from totals object
      const totals = res.totals || {};
      
      return {
        totalCash: parseFloat(totals.CASH?.totalAmount || 0),
        cashCount: parseFloat(totals.CASH?.totalAmount || 0), // Using totalAmount instead of count
        totalChequeAmount: parseFloat(totals.CHEQUE?.totalAmount || 0),
        totalChequeCount: parseFloat(totals.CHEQUE?.totalAmount || 0), // Using totalAmount instead of count
        totalCollection: parseFloat(totals.COLLECTION?.totalAmount || 0),
        collectionCount: parseInt(totals.COLLECTION?.count || 0),
        grandTotal: parseFloat(totals.ALL?.totalAmount || 0),
      };
    } catch (error) {
      console.error('Error fetching cash management:', error);
      // Return default values on error
      return {
        totalCash: 0,
        cashCount: 0,
        totalCollection: 0,
        collectionCount: 0,
        totalChequeCount: 0,
        totalChequeAmount: 0,
        grandTotal: 0
      };
    }
  },

  /**
   * Get expenses data from cash management endpoint
   * This provides: totalExpenseAmount, totalExpenseCount
   */
  getExpenses: async (startDate = null, endDate = null) => {
    try {
      const params = { limit: 10000 };
      // Expenses endpoint uses startDate/endDate with YYYY-MM-DD format (no conversion needed)
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      console.log('Fetching expenses with params:', params);
      const response = await axiosInstance.get('/expenses/s', { params });
      const res = response.data;
      
      console.log('Expenses API response:', res);

      // Extract expense data from summary object
      const summary = res.summary || {};
      
      return {
        totalExpenseAmount: parseFloat(summary.totalExpenseAmount || 0),
        totalExpenseCount: parseInt(summary.totalExpenseCount || 0),
      };
    } catch (error) {
      console.error('Error fetching expenses:', error);
      // Return default values on error
      return {
        totalExpenseAmount: 0,
        totalExpenseCount: 0,
      };
    }
  },

  /**
   * Get total unit cost data
   * This provides: totalUnitCost from sales
   */
  getUnitCost: async (startDate = null, endDate = null) => {
    try {
      const params = {};
      // Unit cost endpoint uses startDate/endDate with YYYY-MM-DD format
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      console.log('Fetching unit cost with params:', params);
      const response = await axiosInstance.get('/sales/total-unit-cost', { params });
      const res = response.data;
      
      console.log('Unit cost API response:', res);

      // Extract unit cost from data object
      const data = res.data || {};
      
      return {
        totalUnitCost: parseFloat(data.totalUnitCost || 0),
      };
    } catch (error) {
      console.error('Error fetching unit cost:', error);
      // Return default values on error
      return {
        totalUnitCost: 0,
      };
    }
  },

  /**
   * Get dashboard statistics for a specific date range
   */
  getDashboardStats: async (startDate = null, endDate = null, userRole = null, userId = null) => {
    const role = normalizeRole(userRole);
    
    console.log('dashboardService.getDashboardStats called with:', {
      startDate,
      endDate,
      role,
      userId,
    });

    try {
      // Fetch credits, cash sales, cash management data, expenses, and unit cost
      const [credits, cashSales, cashManagement, expenses, unitCost] = await Promise.all([
        dashboardService.getCredits(10000, startDate, endDate),
        dashboardService.getCashSales(10000, startDate, endDate),
        dashboardService.getCashManagement(startDate, endDate),
        dashboardService.getExpenses(startDate, endDate),
        dashboardService.getUnitCost(startDate, endDate),
      ]);

      // Calculate dashboard statistics
      const stats = dashboardService.calculateStats(credits, cashSales, cashManagement, expenses, unitCost, startDate, endDate);

      return stats;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  /**
   * Calculate dashboard statistics from raw data
   */
  calculateStats: (credits = [], cashSales = [], cashManagement = {}, expenses = {}, unitCost = {}, startDate = null, endDate = null) => {
    console.log('calculateStats called with:', {
      creditsCount: credits.length,
      cashSalesCount: cashSales.length,
      cashManagement,
      expenses,
      unitCost,
      startDate,
      endDate,
      sampleCredit: credits[0],
      sampleCashSale: cashSales[0]
    });

    // Filter data by date range if provided
    const filterByDateRange = (items, dateField = 'date') => {
      if (!startDate && !endDate) return items;
      
      return items.filter(item => {
        // Try multiple possible date field names
        const possibleDateFields = [dateField, 'createdAt', 'created_at', 'saleDate', 'sale_date', 'transactionDate'];
        let itemDateString = null;
        
        for (const field of possibleDateFields) {
          if (item[field]) {
            itemDateString = item[field];
            break;
          }
        }
        
        if (!itemDateString) {
          console.warn('No date field found in item:', item);
          return false;
        }
        
        // Normalize dates to compare only YYYY-MM-DD (ignore time)
        const itemDate = new Date(itemDateString);
        itemDate.setHours(0, 0, 0, 0);
        
        const start = startDate ? new Date(startDate) : null;
        if (start) start.setHours(0, 0, 0, 0);
        
        const end = endDate ? new Date(endDate) : null;
        if (end) end.setHours(23, 59, 59, 999);

        if (start && itemDate < start) return false;
        if (end && itemDate > end) return false;
        return true;
      });
    };

    const filteredCredits = filterByDateRange(credits);
    const filteredCashSales = filterByDateRange(cashSales);

    console.log('After filtering:', {
      filteredCreditsCount: filteredCredits.length,
      filteredCashSalesCount: filteredCashSales.length
    });

    // Calculate totals from old endpoints (for backwards compatibility)
    const totalCredit = filteredCredits.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const totalCashSales = filteredCashSales.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    // Calculate collections (paid credits) - from cash management data
    const collections = cashManagement.totalCollection || 0;

    // Get cash count and cheque count from cash management endpoint (using totalAmount)
    const cashCount = cashManagement.cashCount || 0;
    const chequeCount = cashManagement.totalChequeCount || 0;
    
    // Get expenses from the new expenses endpoint
    const totalExpenseAmount = expenses.totalExpenseAmount || 0;
    const expenseCount = expenses.totalExpenseCount || 0;

    // Get unit cost from the new unit cost endpoint
    const totalUnitCost = unitCost.totalUnitCost || 0;

    // Calculate total sales (cash + credit)
    const totalSales = (cashManagement.totalCash || 0) + 
                       (cashManagement.totalChequeAmount || 0) + totalCredit;
    
    // Calculate total sales without credit
    const totalSalesWithoutCredit = totalSales - totalCredit;

    // Calculate total profit (Total Sales - Unit Cost)
    const totalProfit = totalSales - totalUnitCost;

    return {
      totalSales,
      totalSalesWithoutCredit,
      cashCount, // From cash management API (totalAmount)
      chequeCount, // From cash management API (totalAmount)
      credit: totalCredit,
      collections, // From cash management API
      expenses: totalExpenseAmount, // From expenses API (/sales/cash-management)
      expenseCount, // From expenses API (/sales/cash-management)
      unitCost: totalUnitCost, // From unit cost API (/sales/total-unit-cost)
      totalProfit, // Calculated as Total Sales - Unit Cost
      
      // Detailed breakdowns
      breakdown: {
        cashSales: totalCashSales,
        creditSales: totalCredit,
        paidCredits: collections,
        unpaidCredits: totalCredit - collections,
      },
      
      // Raw data counts
      counts: {
        totalCashTransactions: filteredCashSales.length,
        totalCreditTransactions: filteredCredits.length,
        cashPayments: cashCount,
        chequePayments: chequeCount,
        expenseCount: expenseCount,
      },
      
      // Cash management specific data
      cashManagementSummary: {
        totalCash: cashManagement.totalCash || 0,
        totalCollection: cashManagement.totalCollection || 0,
        totalChequeAmount: cashManagement.totalChequeAmount || 0,
        totalChequeCount: cashManagement.totalChequeCount || 0,
        totalExpenseAmount: totalExpenseAmount,
        totalExpenseCount: expenseCount,
        grandTotal: cashManagement.grandTotal || 0,
      }
    };
  },

  /**
   * Get dashboard data for preset periods (today, yesterday, last 7 days, this month)
   */
  getStatsByPreset: async (preset = 'today', userRole = null, userId = null) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let startDate, endDate;

    switch (preset) {
      case 'today':
        startDate = today.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = yesterday.toISOString().split('T')[0];
        endDate = yesterday.toISOString().split('T')[0];
        break;
      
      case 'last7':
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);
        startDate = lastWeek.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      
      case 'thisMonth':
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = firstDayOfMonth.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      
      default:
        startDate = null;
        endDate = null;
    }

    return dashboardService.getDashboardStats(startDate, endDate, userRole, userId);
  },

  /**
   * Get comparison data for trend calculations
   * Compares current period with previous period of same length
   */
  getStatsWithTrends: async (startDate, endDate, userRole = null, userId = null) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    // Calculate previous period
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - daysDiff + 1);

    try {
      // Fetch current and previous period data
      const [currentStats, previousStats] = await Promise.all([
        dashboardService.getDashboardStats(
          startDate, 
          endDate, 
          userRole, 
          userId
        ),
        dashboardService.getDashboardStats(
          prevStart.toISOString().split('T')[0],
          prevEnd.toISOString().split('T')[0],
          userRole,
          userId
        ),
      ]);

      // Calculate trends
      const calculateTrend = (current, previous) => {
        if (previous === 0) return { value: 0, isUp: true };
        const change = ((current - previous) / previous) * 100;
        return {
          value: Math.abs(change).toFixed(1),
          isUp: change >= 0
        };
      };

      return {
        current: currentStats,
        previous: previousStats,
        trends: {
          totalSales: calculateTrend(currentStats.totalSales, previousStats.totalSales),
          totalSalesWithoutCredit: calculateTrend(currentStats.totalSalesWithoutCredit, previousStats.totalSalesWithoutCredit),
          credit: calculateTrend(currentStats.credit, previousStats.credit),
          collections: calculateTrend(currentStats.collections, previousStats.collections),
          expenses: calculateTrend(currentStats.expenses, previousStats.expenses),
          cashCount: calculateTrend(currentStats.cashCount, previousStats.cashCount),
          chequeCount: calculateTrend(currentStats.chequeCount, previousStats.chequeCount),
          unitCost: calculateTrend(currentStats.unitCost, previousStats.unitCost),
          totalProfit: calculateTrend(currentStats.totalProfit, previousStats.totalProfit),
        }
      };
    } catch (error) {
      console.error('Error fetching stats with trends:', error);
      throw error;
    }
  },

  /**
   * Get monthly comparison data for charts (last 6 months)
   */
  getMonthlyComparison: async (months = 6, userRole = null, userId = null) => {
    const monthlyData = [];
    const today = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const startDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const endDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

      try {
        const stats = await dashboardService.getDashboardStats(
          startDate.toISOString().split('T')[0],
          endDate.toISOString().split('T')[0],
          userRole,
          userId
        );

        monthlyData.push({
          month: monthDate.toLocaleString('default', { month: 'short' }),
          sales: stats.totalSales,
          expenses: stats.expenses,
          profit: stats.totalSales - stats.expenses,
        });
      } catch (error) {
        console.error(`Error fetching data for month ${i}:`, error);
      }
    }

    return monthlyData;
  },
};

export default dashboardService;
